import { useEffect, useRef, useState } from 'react';
import {
  MdCheck,
  MdExpandMore,
  MdMic,
  MdMicOff,
  MdVideocam,
  MdVideocamOff,
} from 'react-icons/md';
import type { DeviceChoice } from '../lib/mediaDevices';

type Kind = 'camera' | 'microphone';

type Props = {
  kind: Kind;
  enabled: boolean;
  devices: DeviceChoice[];
  selectedId: string | null;
  disabled?: boolean;
  onToggle: () => void;
  onSelect: (deviceId: string) => void;
};

const CLOSE_OTHER_MENUS = 'pulse-close-device-menus';

export function DeviceControl({
  kind,
  enabled,
  devices,
  selectedId,
  disabled,
  onToggle,
  onSelect,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onCloseOthers = (e: Event) => {
      const except = (e as CustomEvent<{ except?: Kind }>).detail?.except;
      if (except !== kind) setOpen(false);
    };
    document.addEventListener(CLOSE_OTHER_MENUS, onCloseOthers);
    return () => document.removeEventListener(CLOSE_OTHER_MENUS, onCloseOthers);
  }, [kind]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isCam = kind === 'camera';
  const toggleLabel = isCam
    ? enabled
      ? 'Turn camera off'
      : 'Turn camera on'
    : enabled
      ? 'Turn microphone off'
      : 'Turn microphone on';
  const menuLabel = isCam ? 'Choose camera' : 'Choose microphone';

  function toggleMenu() {
    document.dispatchEvent(
      new CustomEvent(CLOSE_OTHER_MENUS, { detail: { except: kind } }),
    );
    setOpen((v) => !v);
  }

  const ToggleIcon = isCam
    ? enabled
      ? MdVideocam
      : MdVideocamOff
    : enabled
      ? MdMic
      : MdMicOff;

  return (
    <div
      className={`device-control${enabled ? '' : ' is-off'}`}
      data-device-control={kind}
      ref={rootRef}
    >
      <button
        className={`device-toggle${enabled ? '' : ' is-off'}`}
        type="button"
        aria-label={toggleLabel}
        aria-pressed={!enabled}
        disabled={disabled}
        onClick={onToggle}
      >
        <ToggleIcon aria-hidden="true" />
      </button>
      <span className="status-dot" aria-hidden="true" />
      <button
        className="device-menu-button"
        type="button"
        aria-label={menuLabel}
        aria-expanded={open}
        disabled={disabled || devices.length === 0}
        onClick={(e) => {
          e.stopPropagation();
          toggleMenu();
        }}
      >
        <MdExpandMore aria-hidden="true" />
      </button>
      <div className="device-menu" hidden={!open} role="menu">
        {devices.map((d) => {
          const checked = selectedId ? d.deviceId === selectedId : devices[0]?.deviceId === d.deviceId;
          return (
            <button
              key={d.deviceId || d.label}
              type="button"
              role="menuitemradio"
              aria-checked={checked}
              onClick={() => {
                onSelect(d.deviceId);
                setOpen(false);
              }}
            >
              <span>{d.label}</span>
              {checked ? (
                <span className="device-menu-check" aria-hidden="true">
                  <MdCheck />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
